import { Injectable } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';

/**
 * FocusManagerService
 *
 * Dedicated service for DOM-focus management across the POS dashboard
 * (product/barcode search, quantity/amount/discount inputs, exchange
 * quantity inputs, and dialog focus-trapping helpers).
 *
 * Extracted from DashboardComponent to decouple focus-handling logic while
 * preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class FocusManagerService {
  focusAutoComplete(autoCompleteInput: AutoComplete): void {
    if (
      autoCompleteInput &&
      autoCompleteInput.inputEL &&
      autoCompleteInput.inputEL.nativeElement
    ) {
      autoCompleteInput.inputEL.nativeElement.focus();
    }
  }

  focusAmountInput(): void {
    setTimeout(() => {
      const amountInputs = document.querySelectorAll(
        'input[placeholder="Enter amount"]',
      ) as NodeListOf<HTMLInputElement>;
      if (amountInputs.length > 0) {
        const firstAmountInput = amountInputs[0];
        firstAmountInput.focus();
        firstAmountInput.select();
      }
    }, 50);
  }

  focusQuantityInput(): void {
    setTimeout(() => {
      const quantityInputs = document.querySelectorAll(
        'input.quantity-input',
      ) as NodeListOf<HTMLInputElement>;
      if (quantityInputs.length > 0) {
        const firstQuantityInput = quantityInputs[0];
        firstQuantityInput.focus();
        firstQuantityInput.select();
      }
    }, 50);
  }

  focusDiscountInput(): void {
    setTimeout(() => {
      const discountInputs = document.querySelectorAll(
        'input[placeholder="Discount"]',
      ) as NodeListOf<HTMLInputElement>;
      if (discountInputs.length > 0) {
        const firstDiscountInput = discountInputs[0];
        firstDiscountInput.focus();
        firstDiscountInput.select();
      }
    }, 50);
  }

  // Focus on barcode search input (Ctrl+1)
  focusBarcodeSearch(isBarcodeEnabled: boolean): void {
    // Don't focus if barcode is disabled
    if (!isBarcodeEnabled) {
      return;
    }
    const barcodeInput = document.querySelector(
      '.barcode-input',
    ) as HTMLInputElement;
    if (barcodeInput && !barcodeInput.disabled) {
      barcodeInput.focus();
      barcodeInput.select(); // Select all text for easy replacement
    }
  }

  // Focus on product search input (Ctrl+2)
  focusProductSearch(): void {
    const productInput = document.querySelector(
      '.product-input-container input',
    ) as HTMLInputElement;
    if (productInput && !productInput.disabled) {
      productInput.focus();
      productInput.select(); // Select all text for easy replacement
    }
  }

  // Focus on discount toggle and toggle it (Ctrl+3)
  focusOnDiscountToggle(
    getIsAmountMode: () => boolean,
    setIsAmountMode: (value: boolean) => void,
    onDiscountToggle: (event: { checked: boolean }) => void,
  ): void {
    const selectors = [
      '.discount-toggle-switch',
      '.discount-toggle-switch .p-inputswitch',
      '.discount-toggle-switch input',
      '.discount-switch-container .p-inputswitch',
      '.discount-switch-container input[type="checkbox"]',
    ];

    let discountToggle: HTMLElement | null = null;

    for (const selector of selectors) {
      discountToggle = document.querySelector(selector) as HTMLElement;
      if (discountToggle) {
        break;
      }
    }

    if (discountToggle) {
      discountToggle.focus();

      setIsAmountMode(!getIsAmountMode());
      onDiscountToggle({ checked: getIsAmountMode() });

      const toggleContainer = document.querySelector(
        '.discount-switch-container',
      ) as HTMLElement;
      if (toggleContainer) {
        toggleContainer.style.boxShadow = '0 0 0 3px rgba(33, 150, 243, 0.3)';
        setTimeout(() => {
          toggleContainer.style.boxShadow = '';
        }, 500);
      }
    } else {
      setIsAmountMode(!getIsAmountMode());
      onDiscountToggle({ checked: getIsAmountMode() });

      const toggleContainer = document.querySelector(
        '.discount-switch-container',
      ) as HTMLElement;
      if (toggleContainer) {
        toggleContainer.style.boxShadow = '0 0 0 3px rgba(33, 150, 243, 0.3)';
        setTimeout(() => {
          toggleContainer.style.boxShadow = '';
        }, 500);
      }
    }
  }

  focusFirstExchangeQuantityInput(): void {
    const firstExchangeInput = document.querySelector(
      '.exchange-qty-input',
    ) as HTMLInputElement;
    if (firstExchangeInput) {
      firstExchangeInput.focus();
      firstExchangeInput.select();
    }
  }

  focusFirstDialogElement(): void {
    const selectors: any = [
      '.p-dialog [tabindex]:not([tabindex="-1"])',
      '.p-dialog button:not([disabled])',
      '.p-dialog input:not([disabled])',
      '.p-dialog tr[tabindex]',
      '.p-dialog .p-button',
      'p-dialog [tabindex]:not([tabindex="-1"])',
      'p-dialog button:not([disabled])',
      'p-dialog input:not([disabled])',
      'p-dialog tr[tabindex]',
    ];

    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        const firstElement = elements[0] as HTMLElement;
        firstElement.focus();
        break;
      }
    }
  }

  focusHistoryDialogFirstElement(): void {
    // Try to focus on the Transaction History tab first
    const transactionTab = document.querySelector(
      '.premium-history-dialog .p-tabview-nav li:first-child a',
    ) as HTMLElement;
    if (transactionTab) {
      transactionTab.focus();
      return;
    }

    // Fallback to any focusable element in the dialog
    const dialog = document.querySelector('.premium-history-dialog');
    if (dialog) {
      const focusableElements = dialog.querySelectorAll(
        'a[href]:not([disabled]):not([tabindex="-1"]), ' +
        'button:not([disabled]):not([tabindex="-1"]), ' +
        'input:not([disabled]):not([tabindex="-1"]), ' +
        '[tabindex]:not([tabindex="-1"]):not([disabled])',
      ) as NodeListOf<HTMLElement>;

      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }
}
